import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Marketing from "./pages/Marketing";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Wizard from "./pages/Wizard";
import BuildTrip from "./pages/BuildTrip";
import OrgLanding from "./pages/OrgLanding";
import PublicFeed from "./pages/PublicFeed";
import Platform from "./pages/Platform";
import AppShell from "./components/AppShell";
import Home from "./pages/Home";
import Feed from "./pages/Feed";
import Prayer from "./pages/Prayer";
import Budget from "./pages/Budget";
import Itinerary from "./pages/Itinerary";
import Impact from "./pages/Impact";
import More from "./pages/More";
import Gifts from "./pages/Gifts";
import Partners from "./pages/Partners";
import Settings from "./pages/Settings";
import TripSettings from "./pages/TripSettings";
import Team from "./pages/Team";
import TeamChannel from "./pages/TeamChannel";
import Trips from "./pages/Trips";
import NewTrip from "./pages/NewTrip";
import TripDetail from "./pages/TripDetail";
import TripReport from "./pages/TripReport";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="h-10 w-10 rounded-full border-2 border-t-transparent animate-spin accent-border" />
    </div>
  );
}

function Protected({ children, adminOnly, teamOnly, ownerOnly }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready || user === null) return <Loader />;
  if (!user) {
    const slug = localStorage.getItem("mc_org_slug");
    return <Navigate to={slug ? `/${slug}` : "/auth"} state={{ from: loc }} replace />;
  }
  if (user.role === "platform_admin") return <Navigate to="/platform" replace />;
  if (ownerOnly && !(user.role === "admin" && !user.co_admin)) return <Navigate to="/app" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  if (teamOnly && user.role !== "admin" && user.role !== "team") return <Navigate to="/app" replace />;
  return children;
}

function Routing() {
  const { user, config, ready } = useAuth();
  if (!ready) return <Loader />;
  const authedDest = user && user.role === "platform_admin" ? "/platform"
    : user && localStorage.getItem("mc_pending_wizard") === "1" ? "/wizard" : "/app";
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={authedDest} replace /> : <Landing />} />
      <Route path="/marketing" element={<Marketing />} />
      <Route path="/auth" element={user ? <Navigate to={authedDest} replace /> : <Auth />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/o/:key" element={<OrgLanding />} />
      <Route path="/j/:slug" element={<PublicFeed />} />
      <Route path="/wizard" element={<Protected adminOnly><Wizard /></Protected>} />
      <Route path="/build-trip" element={<Protected adminOnly><BuildTrip /></Protected>} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Home />} />
        <Route path="feed" element={<Feed />} />
        <Route path="prayer" element={<Prayer />} />
        <Route path="budget" element={<Budget />} />
        <Route path="itinerary" element={<Itinerary />} />
        <Route path="impact" element={<Impact />} />
        <Route path="more" element={<More />} />
        <Route path="team" element={<Team />} />
        <Route path="team-channel" element={<Protected teamOnly><TeamChannel /></Protected>} />
        <Route path="gifts" element={<Protected adminOnly><Gifts /></Protected>} />
        <Route path="partners" element={<Protected adminOnly><Partners /></Protected>} />
        <Route path="settings" element={<Protected ownerOnly><Settings /></Protected>} />
        <Route path="trip-settings" element={<Protected adminOnly><TripSettings /></Protected>} />
        <Route path="trips" element={<Protected adminOnly><Trips /></Protected>} />
        <Route path="trips/new" element={<Protected adminOnly><NewTrip /></Protected>} />
        <Route path="trips/:id" element={<Protected adminOnly><TripDetail /></Protected>} />
        <Route path="trips/:id/report" element={<TripReport />} />
      </Route>
      <Route path="/:orgKey" element={<OrgLanding />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routing />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
